import { gql, useQuery } from '@apollo/client'
import { Button, Card, List, Spinner } from '@farol-ds/react'
import { DocDetails } from '@jusbrasil-web/docview/core'
import { RefObject } from 'react'

import {
  DebugInfo_DocumentFragment,
  LoadMotionDebugInfoQuery,
} from './__generated__/debug-info.graphql'
import styles from './debug-info.module.scss'
import { formatAttachmentUrl, renderMetadata, renderPdfInfo } from './functions'

const PLACEHOLDER_SELECTOR = '.entity'

type AnonymizedDataType = {
  'data-index': string
  type: string
  value: string
}

export interface DebugInfoProps {
  document: DebugInfo_DocumentFragment
  contentRef: RefObject<HTMLDivElement>
}

export const LOAD_MOTION_DEBUG_INFO = gql`
  query LoadMotionDebugInfo($docId: NumericID!) {
    root {
      document: documentByNumericID(artifact: "PECA", docId: $docId) {
        attachment {
          storage
          bucket
          key
        }
        anonymizedData
        metadata
        type
        lawsuit {
          number
          relatedPeople {
            name
            role
          }
        }
      }
    }
  }
`

export function DebugInfo(props: DebugInfoProps) {
  const { data, loading } = useQuery<LoadMotionDebugInfoQuery>(LOAD_MOTION_DEBUG_INFO, {
    variables: { docId: props.document.docId },
  })

  if (loading || !data) {
    return <Spinner />
  }

  const { document } = data.root
  if (!document) {
    return null
  }
  const { anonymizedData, attachment, lawsuit, metadata, type } = document

  const attachmentUrl = formatAttachmentUrl(attachment)

  const pairs = [
    {
      label: 'Type',
      value: type,
    },
    {
      label: 'Lawsuit number',
      value: lawsuit?.number,
    },
    {
      label: 'Related people',
      value: (
        <List unstyled={false}>
          {lawsuit?.relatedPeople?.map((person) => (
            <List.Item key={person?.name}>
              {person?.name} [{person?.role}]
            </List.Item>
          ))}
        </List>
      ),
    },
    {
      label: 'Metadata',
      value: renderMetadata(metadata),
    },
    {
      label: 'Attachment URL',
      value: attachmentUrl,
    },
    {
      label: 'PDF info',
      value: renderPdfInfo(attachmentUrl),
    },
  ].filter((pair) => !!pair.value)

  const showAnonymizedData = () => {
    const entities = props.contentRef.current?.querySelectorAll(PLACEHOLDER_SELECTOR) || []
    Array.from(entities).forEach((entity) => {
      const index = entity.getAttribute('data-index')
      if (!index) {
        return
      }
      const data = anonymizedData.find((item: AnonymizedDataType) => item['data-index'] === index)
      if (data) {
        entity.textContent = `${data.value}:${data.type}`
      }
    })
  }

  const enableButton = Boolean(
    Array.isArray(anonymizedData) && anonymizedData.length > 0 && props.contentRef.current
  )

  return (
    <Card className={styles.debugInfo}>
      <DocDetails title="Debug - Admin only" pairs={pairs} />

      {enableButton && (
        <Button kind="primary" onClick={showAnonymizedData} className={styles.button}>
          Show anonymized data
        </Button>
      )}
    </Card>
  )
}

DebugInfo.fragments = {
  document: gql`
    fragment DebugInfo_document on Document {
      docId
    }
  `,
}
